import React, { useState } from 'react';
import { MantineProvider, createTheme, LoadingOverlay } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useParams, Outlet } from 'react-router-dom';
import MantineLoginPage from './components/MantineLoginPage';
import MantineSignUpPage from './components/MantineSignUpPage';
import VerifyEmail from './components/VerifyEmail';
import BusinessInfoPage from './components/BusinessInfoPage';
import AddEmployeesPage from './components/AddEmployeesPage';
import AddServicesPage from './components/AddServicesPage';
import BusinessHoursPage from './components/BusinessHoursPage';
import CalendarView from './components/calendar/CalendarView/CalendarView';
import ClientsList from './components/clients/ClientsList';
import '@mantine/core/styles.css';
import './App.css'
import { AppProvider, useApp } from './context/AppContext';
import ForgotPassword from './components/ForgotPassword';
import CheckoutPage from './components/checkout/CheckoutPage';
import SettingsPage from './components/settings/SettingsPage';
import ChangeTemporaryPassword from './components/ChangeTemporaryPassword';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SalonProvider, useSalon } from './context/SalonContext';
import { CalendarProvider } from './context/CalendarContext';
import { CalendarEventProvider } from './context/CalendarEventContext';
import { ClientProvider } from './context/ClientContext';
import { CampaignProvider } from './context/CampaignContext';
import PaymentsPage from './components/payments/PaymentsPage';
import { StripePaymentsProvider } from './context/StripePaymentsContext';
import AppLayout from './components/Layout/AppLayout';
import MarketingOverview from './components/Marketing/MarketingOverview';
import CampaignWizard from './components/Marketing/CampaignWizard';
import UnauthorizedPage from './components/common/UnauthorizedPage';
import ViewCampaign from './components/Marketing/ViewCampaign';

// Protected Route component
const ProtectedRoute = ({ allowedRoles, children }) => {
  const { currentUser } = useSalon();
  
  if (currentUser && !allowedRoles.includes(currentUser?.role)) {
    return <UnauthorizedPage />;
  }
  
  return children;
};

// Marketing Routes Layout
const MarketingRoutes = () => {
  return (
    <CampaignProvider>
      <Outlet />
    </CampaignProvider>
  );
};

const theme = createTheme({
  primaryColor: 'violet',
  colors: {
    violet: [
      '#6366f1',
      '#f5f3ff',
      '#ede9fe',
      '#ddd6fe',
      '#c4b5fd',
      '#a78bfa',
      '#8b5cf6',
      '#7c3aed',
      '#6d28d9',
      '#5b21b6',
      '#4c1d95',
    ],
  },
  components: {
    Button: {
      defaultProps: {
        color: '#818cf8',
      },
    },
    SegmentedControl: {
      defaultProps: {
        color: '#6366f1',
      },
    },
    TextInput: {
      defaultProps: {
        required: false,
      },
    },
  },
});

function AppContent() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, setIsLoading } = useAuth();
  const { updateSalon, updateStaff } = useApp();
  const { 
    currentUser,
    businessInfo, 
    employees, 
    services, 
    onboardingStage, 
    isMissingSalon, 
    setBusinessInfo, 
    setEmployees, 
    setServices, 
    setOnboardingStage, 
    setIsMissingSalon 
  } = useSalon();
  const [userData, setUserData] = useState();

  const handlePageChange = (page) => {
    navigate(`/${page}`);
  };

  const handleSignUpSubmit = (signupData) => {
    setUserData(signupData);
    navigate('/business-info', { state: { from: 'signup' } });
  };

  const handleEmployeesSubmit = (updatedEmployees) => {
    setIsMissingSalon(false);
    if (updatedEmployees) {
      setEmployees(updatedEmployees);
    }
    navigate('/add-services', { state: { from: 'add-employees' } });
  };

  const handleBusinessHoursSubmit = async (hoursData, isOnboarding = false) => {
    try {
      setIsLoading(true);

      if (!hoursData) {
        if (isOnboarding) {
          navigate('/add-employees', { state: { from: 'business-hours' } });
        }
      } else {
        const salonUpdateResponse = await updateSalon(hoursData);
  
        setBusinessInfo(prevBusinessInfo => ({
          ...prevBusinessInfo,
          ...salonUpdateResponse
        }));
  
        if (isOnboarding) {
          const employeeHoursData = {
            ...Object.entries(hoursData).reduce((acc, [key, value]) => {
              if (key.endsWith('_open_time')) {
                const newKey = key.replace('_open_time', '_start_time');
                return { ...acc, [newKey]: value };
              }
              return { ...acc, [key]: value };
            }, {})
          }
          delete employeeHoursData.id
          
          const employeeUpdateResponse = updateStaff({
            ...employeeHoursData,
          });
          if (employeeUpdateResponse) {
            setEmployees(prevEmployees => [{
              ...prevEmployees[0],
              ...employeeHoursData
            }, ...(prevEmployees.slice(1) || [])]);
            navigate('/add-employees', { state: { from: 'business-hours' } });
          }
        }
      }
    } catch (error) {
      console.error('Error updating salon:', error);
    } finally {
      setIsLoading(false);
      setIsMissingSalon(false);
    }
  };

  const handleServicesSubmit = (isOnboarding = false) => {
    setIsMissingSalon(false);
    setOnboardingStage(null);

    if (isOnboarding) {
      navigate('/admin/calendar', { state: { from: 'add-services' } });
    }
  };

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<MantineLoginPage onPageChange={handlePageChange} />} />
        <Route path="/signup" element={<MantineSignUpPage onSubmit={handleSignUpSubmit} onPageChange={handlePageChange} />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangeTemporaryPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      
      {/* Onboarding Routes */}
      <Route path="/business-info" element={<BusinessInfoPage />} />
      <Route path="/business-hours" element={<BusinessHoursPage onSubmit={handleBusinessHoursSubmit} />} />
      <Route path="/add-employees" element={<AddEmployeesPage onSubmit={handleEmployeesSubmit} />} />
      <Route path="/add-services" element={<AddServicesPage onSubmit={handleServicesSubmit} initialServices={services} />} />
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <AppLayout>
          <Routes>
            <Route path="calendar" element={<CalendarView />} />
            <Route path="clients/*" element={<ClientsList />} />
            <Route path="settings/*" element={<SettingsPage onBusinessHoursSubmit={handleBusinessHoursSubmit} onServicesSubmit={handleServicesSubmit} />} />
            <Route path="payments/*" element={
              <StripePaymentsProvider>
                <PaymentsPage />
              </StripePaymentsProvider>
            } />
            
            {/* Marketing Routes */}
            <Route path="marketing" element={
              <ProtectedRoute allowedRoles={['Owner', 'Manager']}>
                <MarketingRoutes />
              </ProtectedRoute>
            }>
              <Route index element={<MarketingOverview />} />
              <Route path="new" element={<CampaignWizard />} />
              <Route path=":id" element={<ViewCampaign />} />
            </Route>
            
            <Route path="*" element={<Navigate to="/admin/calendar" replace />} />
          </Routes>
        </AppLayout>
      } />
      
      {/* Checkout Route */}
      <Route path="/checkout" element={<CheckoutPage />} />
      
      {/* Default Route */}
      <Route path="*" element={<Navigate to="/admin/calendar" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AuthProvider>
          <SalonProvider>
            <CalendarProvider>
              <CalendarEventProvider>
                <ClientProvider>
                  <MantineProvider theme={theme}>
                    <Notifications />
                    <div className="App">
                      <AppContent />
                    </div>
                  </MantineProvider>
                </ClientProvider>
              </CalendarEventProvider>
            </CalendarProvider>
          </SalonProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
